import { createAppWithRootComponent, OptionalVueProp, VueProp } from "../core/vueHelpers";
import { defineComponent, VNode } from "vue";
import { VueContext } from "../../types";
import Svg from "../core/svg";
import { Key as SvgKey } from "../../types/skyward/svg";

export interface ExternalLinkCardContext extends VueContext
{
    containerData: any;
}

const rootComponent = defineComponent({
    name: "ExternalLinkCard",
    props:
    {
        container: VueProp<JQuery<HTMLElement>>(Object),
        applicationid: OptionalVueProp(Number),
        externallinkid: OptionalVueProp(Number),
        name: VueProp(String),
        description: VueProp(String),
        action: VueProp(String),
        iconurl: VueProp(String),
        url: VueProp(String),
        iconcode: VueProp<SvgKey>(String as () => SvgKey),
        ispinned: VueProp(Boolean),
        isMobile: OptionalVueProp(Boolean),
        loadAction: OptionalVueProp(String),
        showpin: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    render(): VNode[] | any
    {
        return [
            <>
                {this.getCardBody()}
            </>,
            this.applicationid &&
            <a class="externalLinkCardCornerIcon externalLinkCardKeyIcon" href="javascript:void(0)" tabindex="-1">
                <Svg name="Key"
                    svgClasses={["buttonSvg", "externalLinkCardCornerIcon", "svgNoHover"]}
                    tabindex="-1" />
            </a>,
            this.showpin &&
            <a class="externalLinkCardCornerIcon externalLinkCardPinIcon"
                role="button"
                aria-label={`Pin ${this.name} tile`}
                aria-pressed={this.pinnedStatus}
                onClick={(e) => this.togglePinButtonClick(e)}
                href="javascript:void(0)">
                <Svg name={this.pinnedStatus ? "PinFilled" : "Pin"} 
                    svgClasses={["buttonSvg", "externalLinkCardCornerIcon", "svgNoHover"]}
                    tabindex="-1"/>
            </a>
        ].filter(Boolean);;
    },
    data()
    {
        return {
            pinnedStatus: this.ispinned
        };
    },
    mounted()
    {
        const tile = this.container.find(".externalLinkCardContent");
        const nameSpan = $(this.$refs.nameSpan as HTMLElement) as JQuery<HTMLElement>; 
        tile.attr("aria-labelledby", nameSpan.addIds().attr("id") as string);
        const descriptionSpan = $(this.$refs.descriptionSpan as HTMLElement) as JQuery<HTMLElement>;
        tile.attr("aria-describedby", descriptionSpan.addIds().attr("id") as string);

        const vm = this; // Capture the Vue instance

        tile.on('keydown', function (event)
        {
            if ((event.key === 'Enter' || event.keyCode === 13) && event.target === this)
            {
                event.preventDefault();
                vm.openLink()
            }
        });
    },
    computed: {
        smallTileBodyClasses()
        {
            let classes = "widgetTileBody";

            if (this.iconcode)
            {
                classes += " widgetTileBorderHolder";
            }

            return classes;
        }
    }
    ,
    methods:
    {
        getCardBody(): VNode
        {
            if (this.description)
            {
                return this.getDescriptionCard();
            }
            else
            {
                return this.getIconCard();
            }
        },
        getDescriptionCard(): VNode
        {
            const widgetTileBody = this.applicationid
                ? <div class="widgetWideTileBody" aria-label="App with automatic sign-in"> {this.getIcon()} </div>
                : <div class="widgetWideTileBody"> {this.getIcon()} </div>;

            return (
                <a class="externalLinkCardContent externalLinkDescriptionCard" role="link" onClick={() => this.openLink()} tabindex="0">
                    <div class="widgetWideContent">
                        <div class="widgetWideContentInner">
                            {widgetTileBody}
                            <div class="widgetWideTileFooter">
                                <div class="widgetTileFooterLabel">
                                    <span ref="nameSpan" class="widgetTileFooterText">{this.name}</span>
                                </div>
                                <span ref="descriptionSpan" class="externalLinkCardDescription">{this.description}</span>
                            </div>
                        </div>
                    </div>
                </a>
            );
        },
        getIconCard(): VNode
        {
            const iconDiv = this.applicationid
                ? <div class={this.smallTileBodyClasses} aria-label="App with automatic sign-in">{this.getIcon()}</div>
                : <div class={this.smallTileBodyClasses} >{this.getIcon()}</div>;

            return (
                <a class="externalLinkCardContent externalLinkIconCard" role="link" onClick={() => this.openLink()} tabindex="0" >
                    {iconDiv}
                    <div class="widgetTileFooter">
                        <div class="widgetTileFooterLabel">
                            <span ref="nameSpan" class="widgetTileFooterText">{this.name}</span>
                        </div>
                    </div>
                </a>
            );
        },
        getIcon(): VNode
        {
            if (this.iconcode)
            {
                return (
                    <Svg name={this.iconcode} class="svgWidget svgNoHover" preserveAspectRatio="xMidYMin meet"></Svg>
                );
            } else
            {
                return (
                    <img src={this.iconurl} class="svgWidget widgetIcon svgNoHover" />
                );
            }
        },
        async togglePinButtonClick(e: Event)
        {
            e.stopImmediatePropagation();
            await this.togglePin();
        },
        async togglePin()
        {
            let data = {} as any;

            data.pin = !this.pinnedStatus;
            this.pinnedStatus = !this.pinnedStatus;

            if (this.applicationid)
            {
                data.applicationID = this.applicationid;
            } else
            {
                data.externalLinkID = this.externallinkid;
            }

            const result = await $.ajax({
                url: `${window.skyward.global.virtualDir}Security/PinnedApplication/PinExternalLink`,
                data: data,
                type: "POST",
                dataType: "JSON"
            });

            if (result.success)
            {
                this.pinnedStatus = result.isPinned ?? false;
            }
        },
        openLink()
        {
            let url = this.url;

            if (!url.startsWith("http") && !url.startsWith("/"))
            {
                url = `//${url}`;
            }

            window.open(url);
        }
    }
});

export const ExternalLinkCard =
{
    buildVue(context: ExternalLinkCardContext)
    {
        const vm = createAppWithRootComponent(rootComponent,
            {
                ...context.containerData,
                container: $(context.container),
                isMobile: context.isMobile
            })
            .mount(context.container);

        return vm;
    }
};
